import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import api from '@/app/api/axios.js';
import { groupBy } from 'lodash-es';
import { useTeamStore } from '#modules/team/stores/team.store.js';
import { unexpectedError } from '@/shared/utils/unexpected-error.js';
import { useNotificationChannels } from '@/shared/composables/useNotificationChannels.js';

export const useNotificationPreferencesStore = defineStore('notification-preferences', () => {
    const { getNotificationChannels, getNotificationChannelsSettings } = useNotificationChannels();

    const notificationChannels = ref([]);

    const hasSlackIntegration = ref(false);

    const loadNotificationChannels = async () => {
        try {
            const data = await getNotificationChannels();

            notificationChannels.value = data.data;
        } catch (e) {
            unexpectedError(e);
        }
    };

    const loadNotificationChannelsSetting = async () => {
        try {
            const data = await getNotificationChannelsSettings();

            const channelSetting = data.data.find((channel) => channel.relationships.channel.data.id === 'slack');

            if (channelSetting) {
                hasSlackIntegration.value = !!channelSetting.attributes?.slack_webhook_url;
            }
        } catch (e) {
            unexpectedError(e);
        }
    };

    const notificationTypes = ref([]);

    const loadNotificationTypes = async () => {
        try {
            const response = await api.get('/general/notifications/types');

            notificationTypes.value = response.data.data;
        } catch (e) {
            unexpectedError(e);
        }
    };

    const activeTab = ref(null);

    const notificationPreferences = ref([]);
    const notificationTypeGroups = ref([]);

    const loadNotificationPreferences = async () => {
        try {
            const response = await api.get('/general/notifications/preferences');

            notificationTypeGroups.value = response.data.included.filter(
                (item) => item.type === 'notificationTypeGroup'
            );
            notificationPreferences.value = response.data.data;

            if (notificationTypeGroups.value.length > 0) {
                activeTab.value = notificationTypeGroups.value[0].id;
            }
        } catch (e) {
            unexpectedError(e);
        }
    };

    const groupedNotificationPreferences = computed(() => {
        if (notificationPreferences.value.length === 0) {
            return [];
        }

        return groupBy(notificationPreferences.value, function (notificationPreference) {
            return notificationPreference.relationships.notificationTypeGroup.data.id;
        });
    });

    const findNotificationTypeGroup = (id) => {
        return notificationTypeGroups.value.find((item) => item.id === id);
    };

    const isActiveTab = (id) => activeTab.value === id;
    const setActiveTab = (id) => (activeTab.value = id);

    const visibleNotificationPreferences = computed(() => {
        if (!activeTab.value) {
            return [];
        }

        return groupedNotificationPreferences.value[activeTab.value] ?? [];
    });

    const updateNotificationPreference = (opts) => {
        return api.patch('/general/notifications/preferences', {
            notification_type_id: opts.notificationTypeId,
            audience: opts.audience,
            channels: opts.channels,
            user_ids: opts.audience === 'user' ? opts.userIds : [],
        });
    };

    const teamStore = useTeamStore();
    const isLoaded = ref(false);
    const initStore = async () => {
        await Promise.all([
            loadNotificationChannels(),
            loadNotificationChannelsSetting(),
            loadNotificationTypes(),
            loadNotificationPreferences(),
            teamStore.getMembers(),
        ]);

        isLoaded.value = true;
    };

    const dehydrate = () => {
        notificationChannels.value = [];
        hasSlackIntegration.value = false;
        notificationTypes.value = [];
        notificationPreferences.value = [];
        notificationTypeGroups.value = [];
        activeTab.value = null;
        isLoaded.value = false;
    };

    return {
        notificationChannels,
        hasSlackIntegration,
        loadNotificationChannels,
        loadNotificationChannelsSetting,
        loadNotificationTypes,
        loadNotificationPreferences,
        notificationTypes,
        activeTab,
        notificationPreferences,
        notificationTypeGroups,
        groupedNotificationPreferences,
        findNotificationTypeGroup,
        isActiveTab,
        setActiveTab,
        visibleNotificationPreferences,
        updateNotificationPreference,
        isLoaded,
        initStore,
        dehydrate,
    };
});
